// Where a visit came from, so the store links can carry it through to App
// Store Connect and Play Console.
//
// A paid link lands on the website with utm_source, and usually utm_medium and
// utm_campaign. GA4 reads those on its own, but the stores never see the
// website's URL, so without this an install from a ChatGPT ad looks like any
// other homepage install. The first page of the visit remembers the source and
// the store buttons add it to their links.

const STORAGE_KEY = "ss-inbound-source";

// Older links tag the visit with a bare parameter instead of utm_source. They
// keep their tag unchanged so the existing App Store Connect campaigns carry on.
const LEGACY_SOURCES = { fb1: "facebook1", ios_share: "ios_share" };

// Apple accepts up to 40 characters in a campaign token.
const TAG_LENGTH = 40;

const clean = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, TAG_LENGTH);

export const parseInboundSource = (search) => {
  const params = new URLSearchParams(search || "");
  const source = clean(params.get("utm_source"));
  if (source) {
    return {
      source,
      medium: clean(params.get("utm_medium")),
      campaign: clean(params.get("utm_campaign")),
      legacy: false,
    };
  }
  const legacyKey = Object.keys(LEGACY_SOURCES).find((key) => params.has(key));
  if (legacyKey) {
    return {
      source: LEGACY_SOURCES[legacyKey],
      medium: "",
      campaign: "",
      legacy: true,
    };
  }
  return null;
};

// Remembers the inbound source for the rest of the visit. Only a URL that
// carries one writes anything, so moving between pages keeps the first source.
export const rememberInboundSource = (search) => {
  const inbound = parseInboundSource(search);
  if (!inbound || typeof window === "undefined") return inbound;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(inbound));
  } catch (error) {
    // Storage can be blocked; the current page still has the source in its URL.
  }
  return inbound;
};

export const inboundSource = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

// The campaign tag the store links carry. A tagged visit prefixes the page's
// own tag with its source ("chatgpt-web-home"); a legacy link keeps its bare
// tag ("facebook1"). Applying it twice gives the same answer, so a tag that is
// already prefixed passes through unchanged.
export const storeCampaignTag = (pageCampaign, inbound) => {
  const page = clean(pageCampaign);
  if (!inbound || !inbound.source) return page;
  if (inbound.legacy) return inbound.source;
  if (page === inbound.source || page.startsWith(`${inbound.source}-`)) {
    return page;
  }
  return clean(`${inbound.source}-${page}`);
};
